import { ICONS } from 'consts/consts';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { getPrettyDate } from 'utils/util/util';
import SimpleErrorPage from 'views/Error/SimpleErrorPage';
import Loading from 'views/Widgets/Loading';
import FilterablePeopleTable, {
  TALBE_SIZE_NO_LIMIT,
} from 'views/Widgets/People/FilterablePeopleTable';
import {
  FORMAT_AVATAR_GROUP,
  FORMAT_TEXT_ONLY,
} from 'views/Widgets/People/Filters/common';
import CreateDocumentModal from '../DocumentsAdmin/CreateDocumentModal';

const formatVisibility = (visibility: string) => (
  <>
    <i
      className={
        visibility === 'P'
          ? ICONS.PUBLIC
          : visibility === 'I'
            ? ICONS.PRIVATE
            : ICONS.FILTER
      }
    />{' '}
    {visibility === 'P' ? (
      <FormattedMessage
        id="app.views.ai.agents.agents_admin.document_selector.visibility.public"
        defaultMessage="Public"
      />
    ) : visibility === 'I' ? (
      <FormattedMessage
        id="app.views.ai.agents.agents_admin.document_selector.visibility.private"
        defaultMessage="Private"
      />
    ) : (
      <FormattedMessage
        id="app.views.ai.agents.agents_admin.document_selector.visibility.custom"
        defaultMessage="Custom"
      />
    )}
  </>
);

interface Document {
  id: string;
  name: string;
  description?: string;
  owners?: unknown[];
  visibility?: string;
  updated_at?: string;
}

interface DocumentSelectorProps {
  value?: Document[];
  onChange?: (event: Document[]) => void;
  name?: string;
  agent?: {
    associated_documents?: Document[];
  };
}

const DocumentSelector: React.FC<DocumentSelectorProps> = ({
  value,
  onChange,
  name,
}) => {
  const { formatMessage, locale } = useIntl();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateDocModalOpen, setIsCreateDocModalOpen] = useState(false);
  const [selectedDocumentsSet, setSelectedDocumentsSet] = useState<Set<string>>(
    new Set()
  );

  // Initialize selected documents set when value changes
  useEffect(() => {
    if (Array.isArray(value) && value.length > 0) {
      setSelectedDocumentsSet(new Set(value.map((doc) => doc.id)));
    } else {
      setSelectedDocumentsSet(new Set());
    }
  }, [value]);

  const { data, error, status } = useConfirmApi<{
    results: Document[];
    next?: string;
    count: number;
  }>({
    method: 'GET',
    url: 'documents',
    disabled: !isModalOpen,
  });

  const toggleModal = useCallback(() => {
    if (!isModalOpen) {
      // Reset selection to current value when opening modal
      if (Array.isArray(value) && value.length > 0) {
        setSelectedDocumentsSet(new Set(value.map((doc) => doc.id)));
      } else {
        setSelectedDocumentsSet(new Set());
      }
    }
    setIsModalOpen((prev) => !prev);
  }, [isModalOpen, value]);

  const toggleSelectedRowField = useCallback((rowId: string) => {
    setSelectedDocumentsSet((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);

  const handleSave = useCallback(() => {
    if (!onChange || !name) {
      return;
    }

    const allDocuments = data?.results || [];
    const selectedDocs = allDocuments.filter((doc) =>
      selectedDocumentsSet.has(doc.id)
    );

    onChange(selectedDocs);

    setIsModalOpen(false);
  }, [onChange, name, data, selectedDocumentsSet]);

  // Handle various value types - form might pass object, array, null, or undefined
  const currentValue = useMemo(
    () => (Array.isArray(value) ? value : []),
    [value]
  );

  const columns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.name',
          defaultMessage: 'Name',
        }),
        field: 'name',
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.description',
          defaultMessage: 'Description',
        }),
        field: 'description',
        className: 'col-2',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.owners',
          defaultMessage: 'Owners',
        }),
        field: 'owners',
        className: 'col-1',
        sortable: false,
        format: FORMAT_AVATAR_GROUP,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.visibility',
          defaultMessage: 'Visibility',
        }),
        field: 'visibility',
        className: 'col-1',
        sortable: false,
        format: formatVisibility,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.updated_at',
          defaultMessage: 'Last Updated',
        }),
        field: 'updated_at',
        format: (date: string) => {
          return getPrettyDate({
            dateString: date,
            includeTime: true,
            locale,
          });
        },
        className: 'col-2',
        sortable: false,
      },
    ],
    [formatMessage, locale]
  );

  const getUniqueRowId = useCallback((row: Document) => row.id, []);

  const handleOpenCreateDocModal = useCallback(
    () => setIsCreateDocModalOpen(true),
    []
  );
  const handleCloseCreateDocModal = useCallback(
    () => setIsCreateDocModalOpen(false),
    []
  );
  const handleCreateDocSuccess = useCallback(
    (createdDocument: any) => {
      setIsCreateDocModalOpen(false);
      if (onChange && name) {
        onChange([...currentValue, createdDocument]);
      }
    },
    [onChange, name, currentValue]
  );
  const stopPropagation = useCallback(
    (e: React.SyntheticEvent) => e.stopPropagation(),
    []
  );

  const isRowSelectable = useCallback(() => true, []);

  // Columns for the modal with checkbox support
  const modalColumns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.name',
          defaultMessage: 'Name',
        }),
        field: 'person',
        className: 'col-1',
        sortable: false,
        format: FORMAT_TEXT_ONLY,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.description',
          defaultMessage: 'Description',
        }),
        field: 'description',
        className: 'col-2',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.owners',
          defaultMessage: 'Owners',
        }),
        field: 'owners',
        className: 'col-1',
        sortable: false,
        format: FORMAT_AVATAR_GROUP,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.visibility',
          defaultMessage: 'Visibility',
        }),
        field: 'visibility',
        className: 'col-1',
        sortable: false,
        format: formatVisibility,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.updated_at',
          defaultMessage: 'Last Updated',
        }),
        field: 'updated_at',
        format: (date: string) => {
          return getPrettyDate({
            dateString: date,
            includeTime: true,
            locale,
          });
        },
        className: 'col-2',
        sortable: false,
      },
    ],
    [formatMessage, locale]
  );

  // Transform documents to include a fake person object for checkbox rendering
  const modalRows = useMemo(() => {
    if (!data?.results) return [];

    return data.results.map((doc) => ({
      ...doc,
      person: {
        id: doc.id,
        full_name: doc.name,
        avatar: null,
      },
    }));
  }, [data]);

  return (
    <>
      <div className="mb-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <Button color="primary" onClick={toggleModal} size="sm">
            <i className={ICONS.RESUME + ' me-2'} />
            <FormattedMessage
              id="app.views.ai.agents.agents_admin.document_selector.edit_documents"
              defaultMessage="Edit Documents"
            />
          </Button>
          <Button
            color="white"
            className="border"
            onClick={handleOpenCreateDocModal}
            size="sm"
          >
            <i className="fe fe-plus me-1" />
            <FormattedMessage
              id="app.views.ai.agents.agents_admin.document_selector.create_new"
              defaultMessage="New Document"
            />
          </Button>
          {currentValue.length > 0 && (
            <span className="text-muted small">
              <FormattedMessage
                id="app.views.ai.agents.agents_admin.document_selector.selected_count"
                defaultMessage="{count} {count, plural, one {document} other {documents}} selected"
                values={{ count: currentValue.length }}
              />
            </span>
          )}
        </div>
      </div>

      <div>
        {currentValue.length === 0 ? (
          <p className="text-muted small">
            <FormattedMessage
              id="app.views.ai.agents.agents_admin.document_selector.no_documents"
              defaultMessage="No documents selected. Click 'Edit Content' to select documents."
            />
          </p>
        ) : (
          <FilterablePeopleTable
            rows={currentValue}
            columns={columns}
            hideFilters={true}
            hideExportButton={true}
            initialTableSize={TALBE_SIZE_NO_LIMIT}
          />
        )}
      </div>

      <Modal isOpen={isModalOpen} toggle={toggleModal} size="xl">
        <ModalHeader toggle={toggleModal}>
          <FormattedMessage
            id="app.views.ai.agents.agents_admin.document_selector.modal.title"
            defaultMessage="Select Documents"
          />
        </ModalHeader>
        <ModalBody>
          {status === 'LOADING' && (
            <Loading
              layout="overlay"
              message={formatMessage({
                id: 'app.views.ai.agents.agents_admin.document_selector.modal.loading',
                defaultMessage: 'Loading documents...',
              })}
            />
          )}
          {!!error && <SimpleErrorPage error={error} />}
          {status === 'SUCCESS' && data && (
            <>
              <p className="text-muted mb-3">
                <FormattedMessage
                  id="app.views.ai.agents.agents_admin.document_selector.modal.description"
                  defaultMessage="Select documents by clicking the checkboxes on the left. Selected documents will be highlighted."
                />
              </p>
              <FilterablePeopleTable
                rows={modalRows}
                columns={modalColumns}
                hideFilters={false}
                hideExportButton={true}
                initialTableSize={TALBE_SIZE_NO_LIMIT}
                isRowSelectable={isRowSelectable}
                toggleSelectedRowField={toggleSelectedRowField}
                getUniqueRowId={getUniqueRowId}
                selectedRowToggleFieldsSet={selectedDocumentsSet}
                setSelectedRowToggleFieldsSet={setSelectedDocumentsSet}
              />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            <FormattedMessage
              id="app.views.ai.agents.agents_admin.document_selector.modal.cancel"
              defaultMessage="Cancel"
            />
          </Button>
          <Button color="primary" onClick={handleSave}>
            <FormattedMessage
              id="app.views.ai.agents.agents_admin.document_selector.modal.save"
              defaultMessage="Save"
            />
          </Button>
        </ModalFooter>
      </Modal>
      {isCreateDocModalOpen && (
        <div onSubmit={stopPropagation} onClick={stopPropagation}>
          <CreateDocumentModal
            isOpen={isCreateDocModalOpen}
            toggle={handleCloseCreateDocModal}
            onSuccess={handleCreateDocSuccess}
          />
        </div>
      )}
    </>
  );
};

export default DocumentSelector;
